/**
 * JourneyAnticipation — the empty-state shown when a Client Hub is live but has no entries yet.
 * Handed to a prospect right after lead conversion, before the proposal lands. Dark, atmospheric,
 * slow ambient glow; the headline/body fade and rise in. Personalized to the project (its name is
 * the headline — a generic "something is coming" line reads gimmicky once you're expecting an
 * actual named project) with an inline invitation to subscribe to update emails, since this is the
 * first moment the prospect has the link and the most natural point to ask.
 */
import { useEffect, useState } from 'react'
import { subscribeToJourney } from '../services/journey.js'

const EYEBROW = 'ALDER JAMES ARCHITECTURE'
const FALLBACK_HEADLINE = 'YOUR PROJECT'
const BODY = 'Your private space for this project with Alder James. We are preparing what comes next — when it is ready, it will appear here, yours to explore.'
const CONTACT = 'hello@alderjames.com    |    949-529-9070'

export default function JourneyAnticipation({ project, previewMode, token }) {
  const [shown, setShown] = useState(false)
  useEffect(() => { const t = setTimeout(() => setShown(true), 60); return () => clearTimeout(t) }, [])

  const headline = (project && project.name ? project.name : FALLBACK_HEADLINE).toUpperCase()

  function enter(delay) {
    return {
      opacity: shown ? 1 : 0,
      transform: shown ? 'translateY(0)' : 'translateY(14px)',
      filter: shown ? 'blur(0px)' : 'blur(10px)',
      transition: `opacity 1.5s ease ${delay}s, transform 1.5s ease ${delay}s, filter 1.5s ease ${delay}s`,
    }
  }

  return (
    <div style={S.wrap}>
      <style>{KEYFRAMES}</style>
      <div style={S.glow} className="aj-janticip-glow" />
      <div style={S.vignette} />

      <div style={S.center}>
        {previewMode && <div style={S.previewTag}>Preview · no entries yet</div>}
        <div style={{ ...S.eyebrow, ...enter(0.15) }}>{EYEBROW}</div>
        {/* letter-spacing is constant (not animated): unlike a fixed two-word phrase, a project
            name is arbitrary length, so animating a width-affecting property risks the same
            wrap/unwrap jump fixed elsewhere for content we can't predict ahead of time. */}
        <div style={{ ...S.headline, ...enter(0.45) }}>{headline}</div>
        <p style={{ ...S.body, ...enter(1.0) }}>{BODY}</p>
        {token && !previewMode && <div style={enter(1.35)}><AnticipationSubscribe token={token} /></div>}
        <div style={{ ...S.contact, ...enter(1.6) }}>{CONTACT}</div>
      </div>
    </div>
  )
}

function AnticipationSubscribe({ token }) {
  const [email, setEmail] = useState('')
  const [state, setState] = useState('idle')   // idle | sending | done | error
  const [msg, setMsg] = useState('')

  async function submit(e) {
    e.preventDefault()
    if (state === 'sending') return
    const v = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { setState('error'); setMsg('Enter a valid email.'); return }
    setState('sending'); setMsg('')
    try { await subscribeToJourney(token, v); setState('done') }
    catch (err) { setState('error'); setMsg(err.message || 'Could not subscribe.') }
  }

  if (state === 'done') {
    return (
      <div style={S.subWrap}>
        <div style={S.subTitle}>You're subscribed</div>
        <div style={S.subNote}>We'll email you the moment there's something to see.</div>
      </div>
    )
  }
  return (
    <form style={S.subWrap} onSubmit={submit}>
      <div style={S.subTitle}>Get an email when it's ready</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'center' }}>
        <input type="email" value={email} placeholder="you@email.com"
          onChange={ev => { setEmail(ev.target.value); if (state === 'error') setState('idle') }}
          style={S.subInput} />
        <button type="submit" disabled={state === 'sending'} style={S.subBtn}>
          {state === 'sending' ? '…' : 'Notify me'}
        </button>
      </div>
      {state === 'error' && <div style={S.subErr}>{msg}</div>}
    </form>
  )
}

const KEYFRAMES = `
@keyframes ajJanticipBreathe {
  0%   { opacity: 0.55; transform: translate(-50%, -50%) scale(1); }
  50%  { opacity: 0.85; transform: translate(-50%, -50%) scale(1.12); }
  100% { opacity: 0.55; transform: translate(-50%, -50%) scale(1); }
}`

const WHITE = a => `rgba(255,255,255,${a})`
const S = {
  wrap: {
    // No background of its own — JourneyPage always renders the animated <MistyBackground/>
    // canvas behind this (for every status, including the entries-empty case that renders this
    // component), so leaving this transparent lets that same mist show through here exactly as
    // it does once entries exist. An opaque gradient here would just paint over it.
    position: 'fixed', inset: 0, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: '"DM Sans", sans-serif',
  },
  glow: {
    position: 'absolute', top: '42%', left: '50%', width: '70vw', height: '70vw', maxWidth: 900, maxHeight: 900,
    borderRadius: '50%', pointerEvents: 'none',
    background: 'radial-gradient(circle, rgba(60,140,160,0.30) 0%, rgba(20,80,95,0.12) 40%, rgba(0,0,0,0) 70%)',
    animation: 'ajJanticipBreathe 9s ease-in-out infinite',
  },
  vignette: { position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse at center, transparent 30%, rgba(5,22,27,0.45) 72%, rgba(5,22,27,0.82) 100%)' },
  center: { position: 'relative', zIndex: 2, textAlign: 'center', padding: '24px', maxWidth: 760 },
  previewTag: { fontFamily: '"DM Mono", monospace', fontSize: 11, letterSpacing: '0.1em', color: WHITE(0.4), marginBottom: 24 },
  eyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 11, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: WHITE(0.42), marginBottom: 26 },
  headline: { fontFamily: '"Montserrat", sans-serif', fontSize: 'clamp(30px, 6vw, 62px)', fontWeight: 200, lineHeight: 1.18, letterSpacing: '0.05em', color: WHITE(0.92), textTransform: 'uppercase' },
  body: { fontFamily: '"DM Sans", sans-serif', fontSize: 'clamp(13px, 1.5vw, 15px)', lineHeight: 1.8, color: WHITE(0.5), maxWidth: 460, margin: '30px auto 0', fontWeight: 300 },
  subWrap: { marginTop: 34 },
  subTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: WHITE(0.55) },
  subNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: WHITE(0.4), marginTop: 6, lineHeight: 1.5 },
  subInput: { width: 220, padding: '9px 12px', borderRadius: 6, border: '0.5px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, outline: 'none' },
  subBtn: { flexShrink: 0, padding: '9px 16px', borderRadius: 6, border: 'none', background: 'rgba(255,255,255,0.92)', color: '#07272D', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  subErr: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#f0a392', marginTop: 8 },
  contact: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, letterSpacing: '0.06em', color: WHITE(0.32), marginTop: 40 },
}
